import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { NgxUiLoaderModule, NgxUiLoaderService } from 'ngx-ui-loader';

@Component({
  selector: 'app-supplier-list',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, NgxUiLoaderModule],
  templateUrl: './supplier-list.component.html',
  styleUrls: ['./supplier-list.component.css']
})
export class SupplierListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLDivElement>;
  suppliers: any[] = [];
  loading = false;
  showAddForm = false;
  
  // Pagination (Infinite Scroll)
  currentPage = 1;
  itemsPerPage = 5; // Show 5 items per load
  totalPages = 1;
  totalItems = 0;
  isLoadingMore = false; // Track if loading more data
  hasMoreData = true; // Track if there's more data to load
  showEditModal = false;
  showResetPasswordModalFlag = false;
  showViewModal = false;
  showSuccessModal = false;
  successModalMessage = '';
  editingSupplier: any = null;
  resetPasswordSupplier: any = null;
  resetPasswordForm = {
    password: ''
  };
  passwordError = '';
  resetPasswordNotification = '';
  resetPasswordNotificationType: 'success' | 'error' | null = null;
  // Field validation errors for Add Supplier form
  companyNameError = '';
  firstNameError = '';
  emailError = '';
  phoneError = '';
  addressLine1Error = '';
  cityError = '';
  stateError = '';
  countryError = '';
  pincodeError = '';
  passwordErrorAdd = '';
  supplierForm = {
    companyName: '',
    firstName: '',
    lastName: '',
    role: 'Supplier',
    email: '',
    phone: '',
    phoneCountryCode: 'IN +91',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    password: ''
  };
  editForm = {
    companyName: '',
    firstName: '',
    lastName: '',
    role: 'Supplier',
    email: '',
    phone: '',
    phoneCountryCode: 'IN +91',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    country: 'India',
    pincode: ''
  };
  countryCodes = [
    { code: 'IN +91', label: 'IN +91' },
    { code: 'US +1', label: 'US +1' },
    { code: 'UK +44', label: 'UK +44' },
    { code: 'CN +86', label: 'CN +86' }
  ];
  states = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];
  countries = ['India', 'USA', 'UK', 'China', 'Canada', 'Australia'];
  formLoading = false;
  error = '';
  success = '';
  addSupplierError = ''; // Error specific to add supplier form
  successMessage = ''; // Success notification message
  errorMessage = ''; // Error notification message
  showPassword = false;
  
  // Search and filters
  searchTerm = '';
  selectedCountry = '';
  selectedStatus = '';
  showActionMenu: { [key: string]: boolean } = {};

  private scrollHandler: any = null;
  private scrollableParent: HTMLElement | null = null;

  constructor(
    private apiService: ApiService,
    private ngxLoader: NgxUiLoaderService,
    private router: Router,
    public authService: AuthService,
    private elementRef: ElementRef
  ) {}

  ngOnInit() {
    this.loadSuppliers();
    // Wait for view to initialize, then attach scroll listener to table container
    setTimeout(() => {
      this.attachScrollListener();
    }, 100);
  }

  ngAfterViewInit() {
    // Attach scroll listener after view is initialized
    this.attachScrollListener();
  }

  ngOnDestroy() {
    if (this.scrollHandler) {
      if (this.scrollableParent) {
        this.scrollableParent.removeEventListener('scroll', this.scrollHandler);
      }
      if (this.tableContainer?.nativeElement) {
        this.tableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
      }
    }
  }

  /** Find the scrollable ancestor (e.g. main-content) so we can listen to page scroll */
  private findScrollableParent(): HTMLElement | null {
    let el: HTMLElement | null = this.elementRef.nativeElement;
    while (el && el !== document.body) {
      el = el.parentElement;
      if (!el) break;
      const style = getComputedStyle(el);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') {
        return el;
      }
    }
    return null;
  }

  attachScrollListener() {
    if (!this.scrollHandler) {
      this.scrollHandler = this.throttle(() => this.onScroll(), 200);
    }

    // Listen to the scrollable page container (main-content) so scrolling the page triggers load more
    if (!this.scrollableParent) {
      this.scrollableParent = this.findScrollableParent();
    }
    if (this.scrollableParent) {
      this.scrollableParent.removeEventListener('scroll', this.scrollHandler);
      this.scrollableParent.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    // Also listen to table container scroll (inner box) when it exists
    if (this.tableContainer?.nativeElement) {
      this.tableContainer.nativeElement.removeEventListener('scroll', this.scrollHandler);
      this.tableContainer.nativeElement.addEventListener('scroll', this.scrollHandler, { passive: true });
    }
  }

  // Throttle function to limit scroll event calls
  private throttle(func: Function, limit: number) {
    let inThrottle: boolean;
    return function(this: any, ...args: any[]) {
      if (!inThrottle) {
        func.apply(this, ...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  onScroll() {
    const threshold = 150;

    const isNearBottom = (el: HTMLElement): boolean => {
      const scrollTop = el.scrollTop;
      const scrollHeight = el.scrollHeight;
      const clientHeight = el.clientHeight;
      return scrollHeight - (scrollTop + clientHeight) <= threshold;
    };

    let nearBottom = false;
    if (this.scrollableParent && this.scrollableParent.scrollHeight > this.scrollableParent.clientHeight) {
      nearBottom = isNearBottom(this.scrollableParent);
    }
    if (!nearBottom && this.tableContainer?.nativeElement) {
      const tc = this.tableContainer.nativeElement;
      if (tc.scrollHeight > tc.clientHeight) {
        nearBottom = isNearBottom(tc);
      }
    }

    if (nearBottom && this.hasMoreData && !this.isLoadingMore && !this.loading) {
      this.loadMoreSuppliers();
    }
  }

  loadMoreSuppliers() {
    // Don't load if already loading or no more data
    if (this.isLoadingMore || !this.hasMoreData || this.loading) {
      console.log('loadMoreSuppliers blocked:', {
        isLoadingMore: this.isLoadingMore,
        hasMoreData: this.hasMoreData,
        loading: this.loading
      });
      return;
    }

    // Check if there are more pages
    if (this.currentPage < this.totalPages) {
      console.log('Loading more suppliers - Current page:', this.currentPage, 'Total pages:', this.totalPages);
      this.isLoadingMore = true;
      this.ngxLoader.start(); // Start loader for infinite scroll
      const nextPage = this.currentPage + 1;
      this.currentPage = nextPage;
      
      const status = this.selectedStatus || undefined;
      const country = this.selectedCountry || undefined;
      const search = this.searchTerm || undefined;
      
      console.log('Calling API for page:', nextPage, 'limit:', this.itemsPerPage);
      this.apiService.getSuppliers(nextPage, this.itemsPerPage, search, status, country).subscribe({
        next: (response) => {
          if (response.suppliers && response.pagination) {
            // Fetch statistics for new suppliers
            this.apiService.getSupplierStats().subscribe({
              next: (statsData) => {
                const statsMap = new Map();
                statsData.forEach((stat: any) => {
                  statsMap.set(stat.supplierId.toString(), stat);
                });
                
                // Map new suppliers with statistics
                const newSuppliers = response.suppliers.map((supplier: any, index: number) => {
                  const stats = statsMap.get(supplier._id.toString()) || { sharedCategoriesCount: 0, activeQuotesCount: 0 };
                  // Use supplierId from database if available, otherwise fallback to generated ID
                  let supplierId = supplier.supplierId;
                  if (!supplierId) {
                    supplierId = supplier._id ? `S${supplier._id.toString().slice(-6)}` : `S${String(index + 1).padStart(6, '0')}`;
                  }
                  return {
                    ...supplier,
                    supplierId: supplierId,
                    role: supplier.userType === 'Supplier' ? 'Supplier' : supplier.userType || 'Supplier',
                    country: supplier.country || 'India',
                    sharedCategoriesCount: stats.sharedCategoriesCount || 0,
                    activeQuotesCount: stats.activeQuotesCount || 0,
                    createdAt: supplier.createdAt || new Date(),
                    updatedAt: supplier.updatedAt || new Date(),
                    isActive: supplier.isActive !== undefined ? supplier.isActive : true
                  };
                });
                
                // Append new suppliers to existing ones
                this.suppliers = [...this.suppliers, ...newSuppliers];
                
                this.currentPage = response.pagination.currentPage;
                this.totalPages = response.pagination.totalPages;
                this.totalItems = response.pagination.totalItems;
                
                // Check if there's more data
                this.hasMoreData = this.currentPage < this.totalPages;
                
                // Re-attach scroll listener after new data loads
                setTimeout(() => {
                  this.attachScrollListener();
                }, 50);
                
                this.isLoadingMore = false;
                this.ngxLoader.stop(); // Stop loader
              },
              error: (statsErr) => {
                console.error('Error loading supplier stats:', statsErr);
                // Continue without stats if stats fail
                const newSuppliers = response.suppliers.map((supplier: any, index: number) => {
                  // Use supplierId from database if available, otherwise fallback to generated ID
                  let supplierId = supplier.supplierId;
                  if (!supplierId) {
                    supplierId = supplier._id ? `S${supplier._id.toString().slice(-6)}` : `S${String(index + 1).padStart(6, '0')}`;
                  }
                  return {
                    ...supplier,
                    supplierId: supplierId,
                    role: supplier.userType === 'Supplier' ? 'Supplier' : supplier.userType || 'Supplier',
                    country: supplier.country || 'India',
                    sharedCategoriesCount: 0,
                    activeQuotesCount: 0,
                    createdAt: supplier.createdAt || new Date(),
                    updatedAt: supplier.updatedAt || new Date(),
                    isActive: supplier.isActive !== undefined ? supplier.isActive : true
                  };
                });
                
                this.suppliers = [...this.suppliers, ...newSuppliers];
                
                this.currentPage = response.pagination.currentPage;
                this.totalPages = response.pagination.totalPages;
                this.totalItems = response.pagination.totalItems;
                this.hasMoreData = this.currentPage < this.totalPages;
                
                setTimeout(() => {
                  this.attachScrollListener();
                }, 50);
                
                this.isLoadingMore = false;
                this.ngxLoader.stop();
              }
            });
          }
        },
        error: (err) => {
          console.error('Error loading more suppliers:', err);
          this.isLoadingMore = false;
          this.ngxLoader.stop(); // Stop loader on error
          this.error = err.error?.message || 'Error loading more suppliers';
          setTimeout(() => {
            this.error = '';
          }, 5000);
        }
      });
    } else {
      this.hasMoreData = false;
    }
  }

  loadSuppliers() {
    this.loading = true;
    this.ngxLoader.start(); // Start loader
    this.error = '';
    this.currentPage = 1; // Reset to first page
    this.hasMoreData = true; // Reset has more data flag
    
    const status = this.selectedStatus || undefined;
    const country = this.selectedCountry || undefined;
    const search = this.searchTerm || undefined;
    
    // Fetch suppliers with pagination
    console.log('Loading suppliers - Page:', this.currentPage, 'Limit:', this.itemsPerPage, 'Search:', search, 'Status:', status, 'Country:', country);
    this.apiService.getSuppliers(this.currentPage, this.itemsPerPage, search, status, country).subscribe({
      next: (response) => {
        console.log('Suppliers API Response:', response);
        console.log('Response has suppliers:', !!response.suppliers);
        console.log('Response has pagination:', !!response.pagination);
        
        if (response.suppliers && response.pagination) {
          console.log('Pagination info:', response.pagination);
          // Fetch statistics
          this.apiService.getSupplierStats().subscribe({
            next: (statsData) => {
              console.log('Supplier stats loaded:', statsData);
              
              // Create a map of stats by supplier ID
              const statsMap = new Map();
              statsData.forEach((stat: any) => {
                statsMap.set(stat.supplierId.toString(), stat);
              });
              
              // Map suppliers with statistics
              // IMPORTANT: Only use the suppliers from the paginated response (should be 5 items)
              const mappedSuppliers = response.suppliers.map((supplier: any, index: number) => {
                const stats = statsMap.get(supplier._id.toString()) || { sharedCategoriesCount: 0, activeQuotesCount: 0 };
                // Use supplierId from database if available, otherwise fallback to generated ID
                let supplierId = supplier.supplierId;
                if (!supplierId) {
                  supplierId = supplier._id ? `S${supplier._id.toString().slice(-6)}` : `S${String(index + 1).padStart(6, '0')}`;
                }
                return {
                  ...supplier,
                  supplierId: supplierId,
                  role: supplier.userType === 'Supplier' ? 'Supplier' : supplier.userType || 'Supplier',
                  country: supplier.country || 'India',
                  sharedCategoriesCount: stats.sharedCategoriesCount || 0,
                  activeQuotesCount: stats.activeQuotesCount || 0,
                  createdAt: supplier.createdAt || new Date(),
                  updatedAt: supplier.updatedAt || new Date(),
                  isActive: supplier.isActive !== undefined ? supplier.isActive : true
                };
              });
              
              // For initial load, replace suppliers. For loadMore, append.
              if (this.currentPage === 1) {
                this.suppliers = mappedSuppliers;
              } else {
                this.suppliers = [...this.suppliers, ...mappedSuppliers];
              }
              
              console.log('Suppliers after mapping:', this.suppliers.length, 'Expected:', response.suppliers.length);
              
              this.currentPage = response.pagination.currentPage;
              this.totalPages = response.pagination.totalPages;
              this.totalItems = response.pagination.totalItems;
              
              // Check if there's more data
              this.hasMoreData = this.currentPage < this.totalPages;
              
              console.log('Pagination state:', {
                currentPage: this.currentPage,
                totalPages: this.totalPages,
                totalItems: this.totalItems,
                hasMoreData: this.hasMoreData,
                suppliersCount: this.suppliers.length
              });
              
              // Re-attach scroll listener after data loads
              setTimeout(() => {
                this.attachScrollListener();
              }, 100);
              
              this.loading = false;
              this.ngxLoader.stop(); // Stop loader
            },
            error: (statsErr) => {
              console.error('Error loading supplier stats:', statsErr);
              // Continue without stats if stats fail
              const mappedSuppliers = response.suppliers.map((supplier: any, index: number) => {
                // Use supplierId from database if available, otherwise fallback to generated ID
                let supplierId = supplier.supplierId;
                if (!supplierId) {
                  supplierId = supplier._id ? `S${supplier._id.toString().slice(-6)}` : `S${String(index + 1).padStart(6, '0')}`;
                }
                return {
                  ...supplier,
                  supplierId: supplierId,
                  role: supplier.userType === 'Supplier' ? 'Supplier' : supplier.userType || 'Supplier',
                  country: supplier.country || 'India',
                  sharedCategoriesCount: 0,
                  activeQuotesCount: 0,
                  createdAt: supplier.createdAt || new Date(),
                  updatedAt: supplier.updatedAt || new Date(),
                  isActive: supplier.isActive !== undefined ? supplier.isActive : true
                };
              });
              
              // For initial load, replace suppliers. For loadMore, append.
              if (this.currentPage === 1) {
                this.suppliers = mappedSuppliers;
              } else {
                this.suppliers = [...this.suppliers, ...mappedSuppliers];
              }
              
              this.currentPage = response.pagination.currentPage;
              this.totalPages = response.pagination.totalPages;
              this.totalItems = response.pagination.totalItems;
              this.hasMoreData = this.currentPage < this.totalPages;
              
              setTimeout(() => {
                this.attachScrollListener();
              }, 100);
              
              this.loading = false;
              this.ngxLoader.stop(); // Stop loader
            }
          });
        } else {
          // Fallback for old API response format or error in response structure
          console.warn('Unexpected response format:', response);
          console.warn('Response is array:', Array.isArray(response));
          this.suppliers = (Array.isArray(response) ? response : (response.suppliers || [])).map((supplier: any, index: number) => {
            // Use supplierId from database if available, otherwise fallback to generated ID
            let supplierId = supplier.supplierId;
            if (!supplierId) {
              supplierId = supplier._id ? `S${supplier._id.toString().slice(-6)}` : `S${String(index + 1).padStart(6, '0')}`;
            }
            return {
              ...supplier,
              supplierId: supplierId,
              role: supplier.userType === 'Supplier' ? 'Supplier' : supplier.userType || 'Supplier',
              country: supplier.country || 'India',
              sharedCategoriesCount: 0,
              activeQuotesCount: 0,
              createdAt: supplier.createdAt || new Date(),
              updatedAt: supplier.updatedAt || new Date(),
              isActive: supplier.isActive !== undefined ? supplier.isActive : true
            };
          });
          this.hasMoreData = false;
          this.loading = false;
          this.ngxLoader.stop(); // Stop loader
        }
      },
      error: (err) => {
        console.error('Error loading suppliers:', err);
        console.error('Error details:', {
          status: err.status,
          statusText: err.statusText,
          error: err.error,
          message: err.message,
          url: err.url
        });
        if (err.status === 0) {
          this.error = 'Cannot connect to server. Please make sure the backend API is running on http://localhost:4000';
        } else if (err.status === 401) {
          this.error = 'Unauthorized. Please login again.';
        } else if (err.status === 403) {
          this.error = 'Access denied. Only Admin and Procurement Manager can view suppliers.';
        } else {
          this.error = err.error?.message || 'Error loading suppliers. Please try again.';
        }
        this.loading = false;
        this.hasMoreData = false;
        this.ngxLoader.stop(); // Stop loader on error
        this.showErrorNotification(this.error);
      }
    });
  }

  onSearch() {
    this.currentPage = 1;
    this.suppliers = []; // Clear existing suppliers
    this.loadSuppliers();
  }

  applyFilters() {
    this.currentPage = 1;
    this.suppliers = []; // Clear existing suppliers
    this.loadSuppliers();
  }

  resetFilters() {
    this.searchTerm = '';
    this.selectedCountry = '';
    this.selectedStatus = '';
    this.currentPage = 1;
    this.suppliers = []; // Clear existing suppliers
    this.loadSuppliers();
  }

  toggleActionMenu(vendorId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    // Close all other menus
    Object.keys(this.showActionMenu).forEach(key => {
      if (key !== vendorId) {
        this.showActionMenu[key] = false;
      }
    });
    this.showActionMenu[vendorId] = !this.showActionMenu[vendorId];
  }

  closeActionMenu() {
    Object.keys(this.showActionMenu).forEach(key => {
      this.showActionMenu[key] = false;
    });
  }

  activateSupplier(supplier: any) {
    this.apiService.updateUserStatus(supplier._id, true).subscribe({
      next: (response) => {
        console.log('Supplier activated:', response);
        this.successModalMessage = 'User has been activated';
        this.showSuccessModal = true;
        this.loadSuppliers();
        this.closeActionMenu();
        setTimeout(() => {
          this.closeSuccessModal();
        }, 3000);
      },
      error: (err) => {
        console.error('Error activating supplier:', err);
        this.error = err.error?.message || 'Error activating supplier. Please try again.';
        setTimeout(() => {
          this.error = '';
        }, 3000);
      }
    });
  }

  deactivateSupplier(supplier: any) {
    this.apiService.updateUserStatus(supplier._id, false).subscribe({
      next: (response) => {
        console.log('Supplier deactivated:', response);
        this.successModalMessage = 'User has been deactivated';
        this.showSuccessModal = true;
        this.loadSuppliers();
        this.closeActionMenu();
        setTimeout(() => {
          this.closeSuccessModal();
        }, 3000);
      },
      error: (err) => {
        console.error('Error deactivating supplier:', err);
        this.error = err.error?.message || 'Error deactivating supplier. Please try again.';
        setTimeout(() => {
          this.error = '';
        }, 3000);
      }
    });
  }

  formatDate(date: any): string {
    if (!date) return '--';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  }

  formatCount(count: number): string {
    return String(count || 0).padStart(2, '0');
  }

  viewSupplier(supplier: any) {
    // Navigate to supplier view details page
    const supplierId = supplier._id || supplier.id || '';
    this.router.navigate(['/supplier/view', supplierId]);
    this.closeActionMenu();
  }

  showAddSupplierForm() {
    this.showAddForm = true;
    this.resetForm();
  }

  resetForm() {
    this.supplierForm = {
      companyName: '',
      firstName: '',
      lastName: '',
      role: 'Supplier',
      email: '',
      phone: '',
      phoneCountryCode: 'IN +91',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      country: 'India',
      pincode: '',
      password: ''
    };
    this.error = '';
    this.success = '';
    this.addSupplierError = '';
    this.successMessage = '';
    this.errorMessage = '';
    this.companyNameError = '';
    this.firstNameError = '';
    this.emailError = '';
    this.phoneError = '';
    this.addressLine1Error = '';
    this.cityError = '';
    this.stateError = '';
    this.countryError = '';
    this.pincodeError = '';
    this.passwordErrorAdd = '';
    this.showPassword = false;
  }

  createSupplier() {
    // Clear all field errors
    this.companyNameError = '';
    this.firstNameError = '';
    this.emailError = '';
    this.phoneError = '';
    this.addressLine1Error = '';
    this.cityError = '';
    this.stateError = '';
    this.countryError = '';
    this.pincodeError = '';
    this.passwordErrorAdd = '';
    this.addSupplierError = '';

    let hasErrors = false;

    // Validate Company Name
    if (!this.supplierForm.companyName || !this.supplierForm.companyName.trim()) {
      this.companyNameError = 'Company Name is required';
      hasErrors = true;
    }

    // Validate First Name
    if (!this.supplierForm.firstName || !this.supplierForm.firstName.trim()) {
      this.firstNameError = 'First Name is required';
      hasErrors = true;
    }

    // Validate Email
    if (!this.supplierForm.email || !this.supplierForm.email.trim()) {
      this.emailError = 'Email ID is required';
      hasErrors = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.supplierForm.email.trim())) {
        this.emailError = 'Please enter a valid email address';
        hasErrors = true;
      }
    }

    // Validate Phone
    if (!this.supplierForm.phone || !this.supplierForm.phone.trim()) {
      this.phoneError = 'Phone No is required';
      hasErrors = true;
    }

    // Validate Address Line 1
    if (!this.supplierForm.addressLine1 || !this.supplierForm.addressLine1.trim()) {
      this.addressLine1Error = 'Address Line 1 is required';
      hasErrors = true;
    }

    // Validate City
    if (!this.supplierForm.city || !this.supplierForm.city.trim()) {
      this.cityError = 'City is required';
      hasErrors = true;
    }

    // Validate State
    if (!this.supplierForm.state || !this.supplierForm.state.trim()) {
      this.stateError = 'State is required';
      hasErrors = true;
    }

    // Validate Country
    if (!this.supplierForm.country || !this.supplierForm.country.trim()) {
      this.countryError = 'Country is required';
      hasErrors = true;
    }

    // Validate Pincode
    if (!this.supplierForm.pincode || !this.supplierForm.pincode.trim()) {
      this.pincodeError = 'Pincode is required';
      hasErrors = true;
    }

    // Validate Password
    if (!this.supplierForm.password || !this.supplierForm.password.trim()) {
      this.passwordErrorAdd = 'Password is required';
      hasErrors = true;
    } else if (this.supplierForm.password.length < 6) {
      this.passwordErrorAdd = 'Password must be at least 6 characters long';
      hasErrors = true;
    }

    if (hasErrors) {
      // Show notification for validation errors
      this.showErrorNotification('Please fill in all required fields correctly');
      return;
    }

    this.formLoading = true;
    this.addSupplierError = '';
    this.success = '';

    // Combine first name and last name
    const fullName = this.supplierForm.lastName 
      ? `${this.supplierForm.firstName.trim()} ${this.supplierForm.lastName.trim()}`.trim()
      : this.supplierForm.firstName.trim();

    // Combine country code and phone
    const phoneNumber = this.supplierForm.phoneCountryCode 
      ? `${this.supplierForm.phoneCountryCode} ${this.supplierForm.phone.trim()}`.trim()
      : this.supplierForm.phone.trim();

    const userData = {
      name: fullName,
      email: this.supplierForm.email.trim().toLowerCase(),
      password: this.supplierForm.password,
      companyName: this.supplierForm.companyName.trim(),
      phone: phoneNumber,
      addressLine1: this.supplierForm.addressLine1.trim(),
      addressLine2: this.supplierForm.addressLine2?.trim() || '',
      city: this.supplierForm.city.trim(),
      state: this.supplierForm.state.trim(),
      country: this.supplierForm.country.trim(),
      pincode: this.supplierForm.pincode.trim(),
      userType: 'Supplier'
    };

    console.log('Creating supplier:', { ...userData, password: '***' });

    this.apiService.createUser(userData).subscribe({
      next: (response) => {
        console.log('Supplier created successfully:', response);
        const successMsg = response.message || 'Supplier created successfully!';
        this.success = successMsg;
        this.showSuccessNotification(successMsg);
        this.formLoading = false;
        this.loadSuppliers();
        setTimeout(() => {
          this.showAddForm = false;
          this.resetForm();
        }, 2000);
      },
      error: (err) => {
        console.error('Error creating supplier:', err);
        let errorMsg = '';
        
        if (err.status === 0) {
          errorMsg = 'Cannot connect to server. Please make sure the backend API is running on http://localhost:4000';
          this.addSupplierError = errorMsg;
        } else if (err.status === 401) {
          errorMsg = 'Unauthorized. Please login again.';
          this.addSupplierError = errorMsg;
        } else if (err.status === 403) {
          errorMsg = 'Access denied. Only Admin can create suppliers.';
          this.addSupplierError = errorMsg;
        } else if (err.status === 400) {
          // Check if it's an email duplicate error
          const errorMessage = err.error?.message || 'Invalid data. Please check your input.';
          if (errorMessage.toLowerCase().includes('email') && (errorMessage.toLowerCase().includes('already exists') || errorMessage.toLowerCase().includes('exists'))) {
            this.emailError = errorMessage;
            errorMsg = errorMessage; // Also show in notification
          } else {
            this.addSupplierError = errorMessage;
            errorMsg = errorMessage;
          }
        } else {
          errorMsg = err.error?.message || err.message || 'Error creating supplier. Please try again.';
          this.addSupplierError = errorMsg;
        }
        
        // Show error notification
        if (errorMsg) {
          this.showErrorNotification(errorMsg);
        }
        
        this.formLoading = false;
      }
    });
  }

  onModalOverlayClick(event: Event) {
    // Only close modal if clicking directly on overlay, not on modal content
    const target = event.target as HTMLElement;
    const currentTarget = event.currentTarget as HTMLElement;
    
    // Find the modal-content element
    const modalContent = currentTarget.querySelector('.modal-content');
    
    // Check if the click is on the overlay itself (not on modal-content or its children)
    // If modal-content exists and contains the target, don't close
    if (modalContent && modalContent.contains(target)) {
      return;
    }
    
    // If click is on overlay itself, close the modal
    if (target === currentTarget || target.classList.contains('modal-overlay')) {
      this.cancelForm();
    }
  }

  onEditModalOverlayClick(event: Event) {
    // Only close modal if clicking directly on overlay, not on modal content
    const target = event.target as HTMLElement;
    const currentTarget = event.currentTarget as HTMLElement;
    
    // Find the modal-content element
    const modalContent = currentTarget.querySelector('.modal-content');
    
    // Check if the click is on the overlay itself (not on modal-content or its children)
    // If modal-content exists and contains the target, don't close
    if (modalContent && modalContent.contains(target)) {
      return;
    }
    
    // If click is on overlay itself, close the modal
    if (target === currentTarget || target.classList.contains('modal-overlay')) {
      this.closeEditModal();
    }
  }

  cancelForm() {
    this.showAddForm = false;
    this.resetForm();
  }

  editSupplier(supplier: any) {
    this.editingSupplier = supplier;
    
    // Clear any previous messages when opening edit modal
    this.error = '';
    this.success = '';
    
    // Split name into first and last name
    const nameParts = (supplier.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Extract country code from phone if present
    let phoneNumber = supplier.phone || '';
    let phoneCountryCode = 'IN +91';
    if (phoneNumber && phoneNumber.includes('+')) {
      const match = phoneNumber.match(/^([A-Z]{2}\s*\+\d+|\+\d+)\s+(.+)$/);
      if (match) {
        phoneCountryCode = match[1].trim();
        phoneNumber = match[2].trim();
      } else {
        const spaceIndex = phoneNumber.indexOf(' ');
        if (spaceIndex > 0) {
          phoneCountryCode = phoneNumber.substring(0, spaceIndex).trim();
          phoneNumber = phoneNumber.substring(spaceIndex + 1).trim();
        }
      }
    }
    
    this.editForm = {
      companyName: supplier.companyName || '',
      firstName: firstName,
      lastName: lastName,
      role: supplier.userType || 'Supplier',
      email: supplier.email || '',
      phone: phoneNumber,
      phoneCountryCode: phoneCountryCode,
      addressLine1: supplier.addressLine1 || '',
      addressLine2: supplier.addressLine2 || '',
      city: supplier.city || '',
      state: supplier.state || '',
      country: supplier.country || 'India',
      pincode: supplier.pincode || ''
    };
    this.showEditModal = true;
    this.closeActionMenu();
  }

  closeEditModal() {
    this.showEditModal = false;
    this.editingSupplier = null;
    this.editForm = {
      companyName: '',
      firstName: '',
      lastName: '',
      role: 'Supplier',
      email: '',
      phone: '',
      phoneCountryCode: 'IN +91',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      country: 'India',
      pincode: ''
    };
    this.error = '';
    this.success = '';
  }

  updateSupplier() {
    // Validate required fields
    if (!this.editForm.companyName || !this.editForm.firstName || !this.editForm.email || 
        !this.editForm.phone || !this.editForm.addressLine1 || !this.editForm.city || 
        !this.editForm.state || !this.editForm.country || !this.editForm.pincode) {
      const errorMsg = 'Please fill in all required fields';
      this.error = errorMsg;
      this.showErrorNotification(errorMsg);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(this.editForm.email)) {
      const errorMsg = 'Please enter a valid email address';
      this.error = errorMsg;
      this.showErrorNotification(errorMsg);
      return;
    }

    this.formLoading = true;
    this.error = '';
    this.success = '';

    // Combine first name and last name
    const fullName = this.editForm.lastName 
      ? `${this.editForm.firstName.trim()} ${this.editForm.lastName.trim()}`.trim()
      : this.editForm.firstName.trim();

    // Combine country code and phone
    const phoneNumber = this.editForm.phoneCountryCode 
      ? `${this.editForm.phoneCountryCode} ${this.editForm.phone.trim()}`.trim()
      : this.editForm.phone.trim();

    const updateData: any = {
      name: fullName,
      email: this.editForm.email.trim().toLowerCase(),
      companyName: this.editForm.companyName.trim(),
      phone: phoneNumber,
      addressLine1: this.editForm.addressLine1.trim(),
      addressLine2: this.editForm.addressLine2?.trim() || '',
      city: this.editForm.city.trim(),
      state: this.editForm.state.trim(),
      country: this.editForm.country.trim(),
      pincode: this.editForm.pincode.trim(),
      userType: 'Supplier'
    };

    console.log('Updating supplier:', updateData);

    this.apiService.updateUser(this.editingSupplier._id, updateData).subscribe({
      next: (response) => {
        console.log('Supplier updated successfully:', response);
        this.error = ''; // Clear any previous errors
        this.formLoading = false;
        this.loadSuppliers();
        
        // Close modal immediately on success
        this.closeEditModal();
        
        // Show success message as notification at top of page
        const successMsg = response.message || 'Supplier updated successfully!';
        this.success = successMsg;
        this.showSuccessNotification(successMsg);
      },
      error: (err) => {
        console.error('Error updating supplier:', err);
        this.success = ''; // Clear any previous success messages
        this.formLoading = false;
        
        // Keep modal open on error - don't close it
        // Set appropriate error message based on error type
        let errorMsg = '';
        if (err.status === 0) {
          errorMsg = 'Cannot connect to server. Please make sure the backend API is running on http://localhost:4000';
        } else if (err.status === 401) {
          errorMsg = 'Unauthorized. Please login again.';
        } else if (err.status === 403) {
          errorMsg = 'Access denied. Only Admin can update suppliers.';
        } else if (err.status === 400) {
          errorMsg = err.error?.message || 'Invalid data. Please check your input.';
        } else if (err.status === 404) {
          errorMsg = 'Supplier not found. Please refresh the page and try again.';
        } else if (err.status === 500) {
          errorMsg = err.error?.message || 'Server error. Please try again later.';
        } else {
          errorMsg = err.error?.message || err.message || 'Error updating supplier. Please try again.';
        }
        
        this.error = errorMsg;
        // Show error notification
        if (errorMsg) {
          this.showErrorNotification(errorMsg);
        }
        // Modal stays open so user can fix the error
      }
    });
  }

  showResetPasswordModal(supplier: any) {
    this.resetPasswordSupplier = supplier;
    this.resetPasswordForm = {
      password: ''
    };
    this.showResetPasswordModalFlag = true;
    this.error = '';
    this.success = '';
    this.passwordError = '';
    this.resetPasswordNotification = '';
    this.resetPasswordNotificationType = null;
    this.closeActionMenu();
  }

  closeResetPasswordModal() {
    this.showResetPasswordModalFlag = false;
    this.resetPasswordSupplier = null;
    this.resetPasswordForm = {
      password: ''
    };
    this.error = '';
    this.success = '';
    this.passwordError = '';
    this.resetPasswordNotification = '';
    this.resetPasswordNotificationType = null;
  }

  onPasswordInput() {
    // Clear error when user starts typing
    if (this.passwordError) {
      this.passwordError = '';
    }
  }

  resetPassword() {
    // Clear all errors
    this.error = '';
    this.passwordError = '';

    // Validate password
    if (!this.resetPasswordForm.password) {
      this.passwordError = 'Password is required';
      return;
    }

    if (this.resetPasswordForm.password.length < 6) {
      this.passwordError = 'Password must be at least 6 characters long';
      return;
    }

    this.formLoading = true;
    this.error = '';
    this.success = '';
    this.passwordError = '';

    const updateData = {
      password: this.resetPasswordForm.password
    };

    this.apiService.updateUser(this.resetPasswordSupplier._id, updateData).subscribe({
      next: (response) => {
        console.log('Password reset successfully:', response);
        this.formLoading = false;
        
        // Clear all errors
        this.error = '';
        this.passwordError = '';
        
        // Show success notification
        this.resetPasswordNotification = 'Password reset successfully!';
        this.resetPasswordNotificationType = 'success';
        
        // Close modal after showing success
        setTimeout(() => {
          this.closeResetPasswordModal();
        }, 500);
        
        // Clear notification after 3 seconds
        setTimeout(() => {
          this.resetPasswordNotification = '';
          this.resetPasswordNotificationType = null;
        }, 3000);
      },
      error: (err) => {
        console.error('Error resetting password:', err);
        this.formLoading = false;
        
        // Clear success notification
        this.resetPasswordNotification = '';
        this.resetPasswordNotificationType = null;
        
        // Determine error message
        let errorMessage = '';
        if (err.status === 400 && err.error?.message) {
          const errorMsg = err.error.message.toLowerCase();
          if (errorMsg.includes('password') && (errorMsg.includes('required') || errorMsg.includes('length') || errorMsg.includes('6'))) {
            // Field-specific validation error - show below field
            this.passwordError = err.error.message;
            return; // Don't show notification for field-specific errors
          } else {
            errorMessage = err.error.message;
          }
        } else if (err.status === 0) {
          errorMessage = 'Cannot connect to server. Please make sure the backend API is running on http://localhost:4000';
        } else if (err.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (err.status === 403) {
          errorMessage = 'Access denied. Only Admin can reset passwords.';
        } else if (err.status === 404) {
          errorMessage = 'Supplier not found. Please refresh the page and try again.';
        } else if (err.status === 500) {
          errorMessage = err.error?.message || 'Server error. Please try again later.';
        } else {
          errorMessage = err.error?.message || err.message || 'Error resetting password. Please try again.';
        }
        
        // Show error notification (modal stays open)
        this.resetPasswordNotification = errorMessage;
        this.resetPasswordNotificationType = 'error';
        this.error = errorMessage;
        
        // Clear notification after 3 seconds
        setTimeout(() => {
          this.resetPasswordNotification = '';
          this.resetPasswordNotificationType = null;
        }, 3000);
      }
    });
  }

  closeSuccessModal() {
    this.showSuccessModal = false;
    this.successModalMessage = '';
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  showSuccessNotification(message: string) {
    this.successMessage = message;
    this.errorMessage = ''; // Clear any error message
    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      this.successMessage = '';
    }, 3000);
  }

  showErrorNotification(message: string) {
    this.errorMessage = message;
    this.successMessage = ''; // Clear any success message
    // Auto-hide notification after 5 seconds (longer for errors)
    setTimeout(() => {
      this.errorMessage = '';
    }, 5000);
  }
}

